/**
 * PreviewTile — a single locked / open / finished tile from the program
 * preview design (Figma 11248:513839 + 11249:513841).
 *
 * Used inside PreviewTileGrid for both:
 *   - Day tiles in the program overview ("Finish The Previous Day…")
 *   - Course tiles in the day-detail drilldown ("Finish The Previous Section…")
 *
 * The lock copy uses a `lockedScope` prop ("Day" / "Section" / etc.) so the
 * same component covers both contexts without forking.
 */

import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import { palette } from '@/styles/figma-tokens';

export type PreviewTileState = 'locked' | 'open' | 'finished';

export interface PreviewTileProps {
  /** Signed-URL image. Falls back to a dark gradient. */
  imageUrl?: string | null;
  /** Bottom-left label ("Day 1", "Module 2", …). */
  label: string;
  state: PreviewTileState;
  /** Word used in the lock copy: "Day" / "Section" / etc. Defaults to "Section". */
  lockedScope?: string;
  /** Why it is locked. 'sequence' (default) means the learner has not reached
   *  it yet; 'no-access' means they were never enrolled — a different problem
   *  wearing the same padlock, which is how a missing enrolment was twice
   *  misread as a sequencing rule (Aleix, 2026-09-10). */
  lockedReason?: 'sequence' | 'no-access';
  /** Click handler — wired only when state ≠ 'locked'. */
  onClick?: () => void;
  /** Stagger delay (seconds). The grid auto-passes this. */
  entranceDelay?: number;
  /** Aspect-ratio utility. Defaults to the tall 2:3 program-tile shape;
      course previews pass a shorter ratio to match the compact course cards. */
  aspectClassName?: string;
}

function LockIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="2" fill="currentColor" />
      <path d="M8 11V7a4 4 0 018 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ArrowOpenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PreviewTile({
  imageUrl,
  label,
  state,
  lockedScope = 'section',
  lockedReason = 'sequence',
  onClick,
  entranceDelay = 0,
  aspectClassName = 'aspect-[2/3]',
}: PreviewTileProps) {
  const reduceMotion = useReducedMotion();
  const interactive = state !== 'locked' && !!onClick;

  return (
    <motion.button
      type="button"
      onClick={interactive ? onClick : undefined}
      disabled={!interactive}
      aria-disabled={state === 'locked'}
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : entranceDelay }}
      className={`group relative ${aspectClassName} w-full rounded-2xl overflow-hidden border-[5px] sm:border-[6px] text-left active:scale-[0.99] transition-[transform,filter] duration-150 border-neutral-200 dark:border-neutral-700 ${
        state === 'locked' ? 'cursor-not-allowed' : 'hover:brightness-105'
      }`}
      style={{
        boxShadow: state === 'locked' ? 'none' : '0 6px 22px rgba(0,0,0,0.18)',
      }}
    >
      {/* Background — image, dimmed if locked */}
      {imageUrl && state !== 'locked' ? (
        <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : imageUrl && state === 'locked' ? (
        <>
          <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
        </>
      ) : (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              state === 'locked'
                ? `linear-gradient(160deg, ${palette.grey['900']} 0%, ${palette.grey['950']} 100%)`
                : `linear-gradient(160deg, ${'rgb(var(--color-primary-900))'} 0%, ${'rgb(var(--color-primary-950))'} 100%)`,
          }}
        />
      )}

      {/* Bottom dark gradient — improves label legibility on full-color tiles */}
      {state !== 'locked' && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-2/5"
          style={{
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)',
          }}
        />
      )}

      {/* Locked overlay — central lock + copy */}
      {state === 'locked' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 px-4 text-center">
          <span className="text-primary-500/85" aria-hidden="true">
            <LockIcon />
          </span>
          <p className="mt-3 text-xs sm:text-[13px] font-semibold leading-snug max-w-[18ch]">
            {lockedReason === 'no-access' ? (
              <>
                This course isn&rsquo;t <strong className="font-extrabold">open to you yet</strong> —
                ask your trainer.
              </>
            ) : (
              <>
                Finish the <strong className="font-extrabold">previous {lockedScope}</strong> to
                access this!
              </>
            )}
          </p>
          <p className="absolute bottom-4 left-5 text-base sm:text-lg font-extrabold text-white/35">
            {label}
          </p>
        </div>
      )}

      {/* Open / finished — label + CTA pill */}
      {state !== 'locked' && (
        <>
          <p className="absolute bottom-[70px] sm:bottom-[78px] left-5 text-lg sm:text-xl font-extrabold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
            {label}
          </p>
          <div className="absolute bottom-3 left-3 right-3">
            {state === 'finished' ? (
              <span
                className="flex flex-row justify-center items-center gap-2 w-full h-[46px] px-2 py-1 rounded-full text-white text-[17px] leading-[22px] font-bold"
                style={{ background: palette.success['500'] }}
              >
                <CheckIcon />
                Finished
              </span>
            ) : (
              <span
                className="flex flex-row justify-center items-center gap-2 w-full h-[46px] px-2 py-1 rounded-full bg-white/[0.24] text-white text-[17px] leading-[22px] font-bold transition-[background-color] duration-150 group-hover:bg-white/30"
                style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              >
                <ArrowOpenIcon />
                Open
              </span>
            )}
          </div>
        </>
      )}
    </motion.button>
  );
}
